import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { orpc } from "~/lib/orpc-client";
import { Skeleton, useToast } from "@alchema/ui";
import { RouteErrorBoundary } from "~/components/RouteErrorBoundary";
import { AiGeneratedBadge } from "~/components/AiGeneratedBadge";
import * as m from "~/paraglide/messages";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type NegotiationScenario =
  | "salary-counter"
  | "geographic-discount"
  | "competing-offer"
  | "downlevel-response"
  | "equity-negotiation"
  | "signing-bonus"
  | "review-timeline";

interface NegotiationScript {
  scenario: NegotiationScenario;
  title: string;
  description: string;
  template: string;
  variables: string[];
  tips: string[];
  dachNotes?: string;
}

/** Deterministic template substitution — not a model-generated output. */
export function renderNegotiationTemplateCopy(
  template: string,
  variableValues: Record<string, string>,
): string {
  let filled = template;
  for (const [key, value] of Object.entries(variableValues)) {
    filled = filled.replaceAll(`{{${key}}}`, value || `{{${key}}}`);
  }
  return filled;
}

export function copyNegotiationTemplate(
  template: string,
  variableValues: Record<string, string>,
  clipboard: Pick<Clipboard, "writeText"> = navigator.clipboard,
): Promise<void> {
  return clipboard.writeText(renderNegotiationTemplateCopy(template, variableValues));
}

// ---------------------------------------------------------------------------
// Route definition
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/negotiation")({
  beforeLoad: async () => {
    await requireAuth();
  },
  component: NegotiationPage,
  pendingComponent: NegotiationSkeleton,
  errorComponent: RouteErrorBoundary,
});

// ---------------------------------------------------------------------------
// Scenario metadata (icons as SVG paths, labels)
// ---------------------------------------------------------------------------

const SCENARIO_META: Record<
  NegotiationScenario,
  { icon: string; color: string }
> = {
  "salary-counter": {
    icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    color: "var(--accent)",
  },
  "geographic-discount": {
    icon: "M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z",
    color: "var(--success)",
  },
  "competing-offer": {
    icon: "M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3",
    color: "var(--warning)",
  },
  "downlevel-response": {
    icon: "M13 17h8m0 0V9m0 8l-8-8-4 4-6-6",
    color: "var(--error)",
  },
  "equity-negotiation": {
    icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
    color: "var(--accent)",
  },
  "signing-bonus": {
    icon: "M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7",
    color: "var(--success)",
  },
  "review-timeline": {
    icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
    color: "var(--warning)",
  },
};

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function NegotiationSkeleton() {
  return (
    <div
      className="space-y-8"
      aria-busy="true"
      role="status"
      aria-label={m.negotiation_loading_aria()}
    >
      <div>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-2 h-4 w-96" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <Skeleton key={i} className="h-40 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function NegotiationPage() {
  const { toast } = useToast();
  const [selectedScenario, setSelectedScenario] =
    React.useState<NegotiationScenario | null>(null);
  const [variableValues, setVariableValues] = React.useState<
    Record<string, string>
  >({});
  const [selectedJobId, setSelectedJobId] = React.useState<string>("");
  const [showAnalyzer, setShowAnalyzer] = React.useState(false);

  // Fetch all templates
  const templatesQuery = useQuery(
    orpc.negotiation.listTemplates.queryOptions({ input: undefined }),
  );

  // Fetch jobs for the analyzer
  const jobsQuery = useQuery(orpc.job.list.queryOptions());

  // Fetch single scenario detail
  const scenarioQuery = useQuery({
    ...orpc.negotiation.getScenario.queryOptions({
      input: { scenario: selectedScenario! },
    }),
    enabled: !!selectedScenario,
  });

  // For-job mutation
  const forJobMutation = useMutation(
    orpc.negotiation.forJob.mutationOptions({
      onError: (err) =>
        toast(err.message || m.negotiation_error_analyze_job(), "error"),
    }),
  );

  const templates = templatesQuery.data ?? [];
  const activeScript: NegotiationScript | undefined =
    scenarioQuery.data?.[0] ?? undefined;

  // Deterministic template copy stays unmarked. Reclassify only if this path
  // starts consuming a distinct model-generated script.
  const copyTemplate = React.useCallback(
    (template: string) => {
      copyNegotiationTemplate(template, variableValues).then(
        () => toast(m.negotiation_copied_to_clipboard(), "success"),
        () => toast(m.negotiation_error_copy(), "error"),
      );
    },
    [variableValues, toast],
  );

  // Reset variable values when scenario changes
  React.useEffect(() => {
    setVariableValues({});
  }, [selectedScenario]);

  // Loading state
  if (templatesQuery.isLoading) {
    return <NegotiationSkeleton />;
  }

  if (templatesQuery.isError) {
    return (
      <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-8 text-center">
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {m.negotiation_error_load_templates()}
        </p>
        <button
          onClick={() => templatesQuery.refetch()}
          className="btn btn-ghost btn-sm mt-3"
        >
          {m.negotiation_try_again()}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-up">
      {/* Header */}
      <div>
        <h1
          className="font-semibold tracking-tight"
          style={{ fontSize: 22, fontFamily: "var(--font-display)", color: "var(--text)" }}
        >
          {m.negotiation_page_title()}
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
          {m.negotiation_page_description()}
        </p>
      </div>

      {/* Job Analyzer Toggle */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
      >
        <button
          onClick={() => setShowAnalyzer(!showAnalyzer)}
          className="flex w-full items-center justify-between p-5 transition-colors hover:bg-[var(--surface-raised)]"
          aria-expanded={showAnalyzer}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-lg"
              style={{ background: "color-mix(in oklch, var(--accent) 12%, transparent)" }}
            >
              <svg
                className="h-4 w-4"
                style={{ color: "var(--accent)" }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
            </div>
            <div className="text-left">
              <p className="text-sm font-medium" style={{ color: "var(--text)" }}>
                {m.negotiation_analyze_job_title()}
              </p>
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                {m.negotiation_analyze_job_description()}
              </p>
            </div>
          </div>
          <svg
            className="h-4 w-4 transition-transform duration-200"
            style={{
              color: "var(--text-muted)",
              transform: showAnalyzer ? "rotate(180deg)" : "rotate(0deg)",
            }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </button>

        {showAnalyzer && (
          <div className="space-y-4 px-5 pb-5 pt-4" style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label
                  htmlFor="job-select"
                  className="mb-1 block text-xs font-medium"
                  style={{ color: "var(--text-muted)" }}
                >
                  {m.negotiation_select_a_job()}
                </label>
                <select
                  id="job-select"
                  value={selectedJobId}
                  onChange={(e) => setSelectedJobId(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 text-sm outline-none transition-colors focus:ring-1 focus:ring-[var(--accent)]"
                  style={{
                    background: "var(--surface-raised)",
                    border: "1px solid var(--border-subtle)",
                    color: "var(--text)",
                  }}
                >
                  <option value="">{m.negotiation_choose_a_job()}</option>
                  {(jobsQuery.data ?? []).map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.title}
                      {job.company ? ` - ${job.company}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => {
                  if (selectedJobId) {
                    forJobMutation.mutate({ jobId: selectedJobId });
                  }
                }}
                disabled={!selectedJobId || forJobMutation.isPending}
                className="rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ background: "var(--accent)", color: "#fff" }}
              >
                {forJobMutation.isPending ? (
                  <span className="flex items-center gap-2">
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {m.negotiation_analyzing()}
                  </span>
                ) : (
                  m.negotiation_analyze()
                )}
              </button>
            </div>

            {forJobMutation.data && (
              <JobAnalysisResults data={forJobMutation.data} />
            )}
          </div>
        )}
      </div>

      {/* Template Gallery */}
      <div>
        <p className="section-label mb-4">{m.negotiation_page_title()}</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <ScenarioCard
              key={t.scenario}
              script={t as NegotiationScript}
              isActive={selectedScenario === t.scenario}
              onClick={() =>
                setSelectedScenario(
                  selectedScenario === t.scenario
                    ? null
                    : (t.scenario as NegotiationScenario),
                )
              }
            />
          ))}
        </div>
      </div>

      {/* Scenario Detail */}
      {selectedScenario && activeScript && (
        <ScenarioDetail
          script={activeScript}
          variableValues={variableValues}
          onVariableChange={(key, value) =>
            setVariableValues((prev) => ({ ...prev, [key]: value }))
          }
          onCopy={() => copyTemplate(activeScript.template)}
          isLoading={scenarioQuery.isLoading}
        />
      )}

      {selectedScenario && scenarioQuery.isLoading && (
        <div
          className="rounded-xl p-6"
          style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
        >
          <Skeleton className="h-6 w-48" />
          <Skeleton className="mt-4 h-32 rounded-lg" />
          <Skeleton className="mt-4 h-4 w-64" />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scenario card
// ---------------------------------------------------------------------------

function ScenarioCard({
  script,
  isActive,
  onClick,
}: {
  script: NegotiationScript;
  isActive: boolean;
  onClick: () => void;
}) {
  const meta = SCENARIO_META[script.scenario];

  return (
    <button
      onClick={onClick}
      className="group relative flex flex-col items-start rounded-xl p-4 text-left transition-all duration-150"
      style={{
        border: `1px solid ${isActive ? meta.color : "var(--border-subtle)"}`,
        background: isActive
          ? `color-mix(in oklch, ${meta.color} 6%, var(--surface))`
          : "var(--surface)",
      }}
      aria-pressed={isActive}
    >
      <div
        className="flex h-8 w-8 items-center justify-center rounded-lg"
        style={{
          background: `color-mix(in oklch, ${meta.color} 12%, transparent)`,
        }}
      >
        <svg
          className="h-4 w-4"
          style={{ color: meta.color }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          {meta.icon.split(" M").map((d, i) => (
            <path
              key={i}
              strokeLinecap="round"
              strokeLinejoin="round"
              d={i === 0 ? d : `M${d}`}
            />
          ))}
        </svg>
      </div>
      <h3 className="mt-3 text-sm font-medium" style={{ color: "var(--text)" }}>
        {script.title}
      </h3>
      <p className="mt-1 text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>
        {script.description}
      </p>
      {script.dachNotes && (
        <span
          className="mt-2 inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium"
          style={{
            background: "color-mix(in oklch, var(--warning) 12%, transparent)",
            color: "var(--warning)",
          }}
        >
          {m.negotiation_dach_badge()}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Scenario detail view
// ---------------------------------------------------------------------------

function ScenarioDetail({
  script,
  variableValues,
  onVariableChange,
  onCopy,
  isLoading,
}: {
  script: NegotiationScript;
  variableValues: Record<string, string>;
  onVariableChange: (key: string, value: string) => void;
  onCopy: () => void;
  isLoading: boolean;
}) {
  if (isLoading) return null;

  // Parse template to highlight {{variable}} placeholders
  const renderTemplate = (template: string) => {
    const parts = template.split(/(\{\{[^}]+\}\})/g);
    return parts.map((part, i) => {
      const match = part.match(/^\{\{([^}]+)\}\}$/);
      if (match) {
        const varName = match[1];
        const value = variableValues[varName];
        if (value) {
          return (
            <span
              key={i}
              className="rounded px-1 font-medium"
              style={{
                background:
                  "color-mix(in oklch, var(--success) 15%, transparent)",
                color: "var(--success)",
              }}
            >
              {value}
            </span>
          );
        }
        return (
          <span
            key={i}
            className="rounded px-1 font-medium"
            style={{
              background:
                "color-mix(in oklch, var(--accent) 15%, transparent)",
              color: "var(--accent)",
            }}
          >
            {`{{${varName}}}`}
          </span>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div
      className="space-y-5 rounded-xl p-6 animate-fade-up"
      style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
      role="region"
      aria-label={`${script.title} details`}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-semibold" style={{ color: "var(--text)" }}>
            {script.title}
          </h2>
          <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
            {script.description}
          </p>
        </div>
        <button
          onClick={onCopy}
          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
          style={{
            border: "1px solid var(--border-subtle)",
            color: "var(--text-muted)",
            background: "transparent",
          }}
          title={m.negotiation_copy_script_tooltip()}
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
          </svg>
          {m.negotiation_copy()}
        </button>
      </div>

      {/* Variable fill mode */}
      {script.variables.length > 0 && (
        <div
          className="rounded-lg p-4 space-y-3"
          style={{ border: "1px solid var(--border-subtle)", background: "var(--surface-raised)" }}
        >
          <p className="section-label">{m.negotiation_fill_in_details()}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {script.variables.map((v) => (
              <div key={v}>
                <label
                  htmlFor={`var-${v}`}
                  className="mb-1 block text-xs font-medium"
                  style={{ color: "var(--text-muted)" }}
                >
                  {v.replace(/_/g, " ")}
                </label>
                <input
                  id={`var-${v}`}
                  type="text"
                  placeholder={`Enter ${v.replace(/_/g, " ").toLowerCase()}...`}
                  value={variableValues[v] ?? ""}
                  onChange={(e) => onVariableChange(v, e.target.value)}
                  className="w-full rounded-lg px-3 py-1.5 text-sm outline-none transition-colors focus:ring-1 focus:ring-[var(--accent)]"
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border-subtle)",
                    color: "var(--text)",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Template script */}
      <div>
        <p className="section-label mb-2">{m.negotiation_script_label()}</p>
        <div
          className="whitespace-pre-wrap rounded-lg p-4 text-sm leading-relaxed"
          style={{
            border: "1px solid var(--border-subtle)",
            background: "var(--surface-raised)",
            color: "var(--text-muted)",
          }}
        >
          {renderTemplate(script.template)}
        </div>
      </div>

      {/* Tips */}
      {script.tips.length > 0 && (
        <div>
          <p className="section-label mb-2">{m.negotiation_tips_label()}</p>
          <ul className="space-y-1.5">
            {script.tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
                <svg
                  className="mt-0.5 h-4 w-4 shrink-0"
                  style={{ color: "var(--success)" }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* DACH notes */}
      {script.dachNotes && (
        <div
          className="rounded-lg p-4"
          style={{
            border: "1px solid color-mix(in oklch, var(--warning) 30%, transparent)",
            background: "color-mix(in oklch, var(--warning) 8%, transparent)",
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <svg className="h-4 w-4" style={{ color: "var(--warning)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--warning)" }}>
              {m.negotiation_dach_specific_notes()}
            </p>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: "var(--text-muted)" }}>
            {script.dachNotes}
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Job analysis results
// ---------------------------------------------------------------------------

export function JobAnalysisResults({
  data,
}: {
  data: {
    scripts: NegotiationScript[];
    signals: {
      isRemote: boolean;
      hasEquity: boolean;
      salaryMentioned: boolean;
    };
  };
}) {
  return (
    <div className="space-y-4 animate-fade-up">
      {/* AI transparency badge (EU AI Act Article 50) */}
      <div className="flex justify-end">
        <AiGeneratedBadge />
      </div>
      {/* Detected signals */}
      <div className="flex flex-wrap gap-2">
        <SignalBadge
          label={m.negotiation_signal_remote()}
          active={data.signals.isRemote}
        />
        <SignalBadge
          label={m.negotiation_signal_equity()}
          active={data.signals.hasEquity}
        />
        <SignalBadge
          label={m.negotiation_signal_salary_mentioned()}
          active={data.signals.salaryMentioned}
        />
        {!data.signals.salaryMentioned && (
          <span
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium"
            style={{
              background:
                "color-mix(in oklch, var(--error) 12%, transparent)",
              color: "var(--error)",
            }}
          >
            <svg
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z"
              />
            </svg>
            {m.negotiation_red_flag_no_salary()}
          </span>
        )}
      </div>

      {/* Applicable scripts */}
      {data.scripts.length > 0 ? (
        <div>
          <p className="section-label mb-2">
            {m.negotiation_relevant_scenarios_detected({ count: data.scripts.length })}
          </p>
          <div className="space-y-2">
            {data.scripts.map((s) => {
              const meta = SCENARIO_META[s.scenario as NegotiationScenario];
              return (
                <div
                  key={s.scenario}
                  className="flex items-center gap-3 rounded-lg p-3"
                  style={{
                    border: "1px solid var(--border-subtle)",
                    background: "var(--surface)",
                  }}
                >
                  <div
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                    style={{
                      background: `color-mix(in oklch, ${meta?.color ?? "var(--accent)"} 12%, transparent)`,
                    }}
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      style={{ color: meta?.color ?? "var(--accent)" }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      {(meta?.icon ?? "").split(" M").map((d, i) => (
                        <path key={i} strokeLinecap="round" strokeLinejoin="round" d={i === 0 ? d : `M${d}`} />
                      ))}
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium" style={{ color: "var(--text)" }}>
                      {s.title}
                    </p>
                    <p className="truncate text-xs" style={{ color: "var(--text-muted)" }}>
                      {s.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {m.negotiation_no_scenarios_detected()}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Signal badge
// ---------------------------------------------------------------------------

function SignalBadge({
  label,
  active,
}: {
  label: string;
  active: boolean;
}) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium"
      style={{
        background: active
          ? "color-mix(in oklch, var(--success) 12%, transparent)"
          : "var(--bg-secondary)",
        color: active ? "var(--success)" : "var(--text-muted)",
      }}
    >
      {active ? (
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      ) : (
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      )}
      {label}
    </span>
  );
}
